import React, { useState } from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ContactModal from './ContactModal';

const SpeakingEvents: React.FC = () => {
  const [contactModalOpen, setContactModalOpen] = useState(false);
  return (
    <Box
      sx={{
        backgroundColor: 'white',
        py: { xs: 8, md: 12 },
      }}
    >
      <Container maxWidth="lg">
        {/* Title with red dot */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: '#dc143c',
              mr: 2,
            }}
          />
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 900,
              fontFamily: '"Impact", sans-serif',
              textTransform: 'uppercase',
            }}
          >
            SPEAKING EVENTS
          </Typography>
        </Box>

        {/* Play Button */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#dc143c',
              color: 'white',
              px: 5,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#b01030',
              },
            }}
          >
            Play Tech Talk Now
          </Button>
        </Box>

        {/* Events List */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          {[
            {
              date: '16—17',
              month: 'Oct, 2025',
              logo: '/logos/seo-irl.svg',
              title: 'AI Summit Latin America',
              location: 'Lima Convention Center, Lima, Peru',
            },
            {
              date: '13—16',
              month: 'Oct, 2025',
              logo: '/logos/highlevel-summit.svg',
              title: 'DevOps Days LATAM 2025',
              location: 'São Paulo, Brazil',
            },
            {
              date: '21st',
              month: 'May, 2025',
              logo: '/logos/pycon.svg',
              title: 'PyCon Latin America',
              location: 'Buenos Aires, Argentina',
            },
          ].map((event, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                p: { xs: 3, md: 4 },
                transition: 'all 0.3s',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              {/* Date */}
              <Box
                sx={{
                  minWidth: { xs: '80px', md: '100px' },
                  textAlign: 'center',
                  borderRight: '1px solid #e0e0e0',
                  pr: { xs: 2, md: 3 },
                  mr: { xs: 2, md: 3 },
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '1.5rem', md: '2rem' },
                    fontWeight: 900,
                    fontFamily: '"Arial Black", sans-serif',
                    lineHeight: 1,
                    mb: 0.5,
                  }}
                >
                  {event.date}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '0.875rem', md: '1rem' },
                    color: '#666',
                  }}
                >
                  {event.month}
                </Typography>
              </Box>

              {/* Logo */}
              <Box
                sx={{
                  minWidth: { xs: '80px', md: '120px' },
                  height: { xs: '60px', md: '80px' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: { xs: 2, md: 4 },
                }}
              >
                <img
                  src={event.logo}
                  alt={event.title}
                  width="120"
                  height="80"
                  loading="lazy"
                  decoding="async"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                />
              </Box>

              {/* Event Info */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { xs: '1.25rem', md: '1.75rem' },
                    fontWeight: 900,
                    fontFamily: '"Arial Black", sans-serif',
                    mb: 0.5,
                  }}
                >
                  {event.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '0.875rem', md: '1rem' },
                    color: '#666',
                  }}
                >
                  {event.location}
                </Typography>
              </Box>

              {/* Arrow */}
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'center',
                  ml: 2,
                }}
              >
                <ArrowForwardIcon
                  sx={{
                    fontSize: '2rem',
                    color: '#dc143c',
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'center',
            alignItems: 'center',
            gap: 3,
            mt: 6,
          }}
        >
          <Button
            sx={{
              color: '#1a1a1a',
              fontWeight: 700,
              fontSize: '1.125rem',
              textDecoration: 'underline',
              textUnderlineOffset: '4px',
              textDecorationThickness: '2px',
              p: 0,
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#dc143c',
              },
            }}
          >
            See More Events
          </Button>

          <Button
            onClick={() => setContactModalOpen(true)}
            sx={{
              color: '#1a1a1a',
              fontWeight: 700,
              fontSize: '1.125rem',
              textDecoration: 'underline',
              textUnderlineOffset: '4px',
              textDecorationThickness: '2px',
              p: 0,
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#dc143c',
              },
            }}
          >
            Book me to speak at your event
          </Button>
        </Box>
      </Container>

      {/* Contact Modal */}
      <ContactModal open={contactModalOpen} onClose={() => setContactModalOpen(false)} type="speaking" />
    </Box>
  );
};

export default SpeakingEvents;